import React from 'react';
import { useTranslation } from 'react-i18next';

import '../../scss/style.scss';

import { ReactComponent as DotPatterIcon } from '../../../../assets/icons/dot-pattern.svg';
import { ReactComponent as FluidPatterIcon } from '../../../../assets/icons/fluid-pattern.svg';
import { ReactComponent as InnerEyeCircle } from '../../../../assets/icons/inner-eye-circle.svg';
import { ReactComponent as InnerEyeRound } from '../../../../assets/icons/inner-eye-round.svg';
import { ReactComponent as InnerEyeSquare } from '../../../../assets/icons/inner-eye-square.svg';
import { ReactComponent as InnerEye } from '../../../../assets/icons/inner-eye.svg';
import { ReactComponent as OuterEyeCircle } from '../../../../assets/icons/outer-eye-circle.svg';
import { ReactComponent as OuterEyeRound } from '../../../../assets/icons/outer-eye-round.svg';
import { ReactComponent as OuterEyeSquare } from '../../../../assets/icons/outer-eye-square.svg';
import { ReactComponent as OuterEye } from '../../../../assets/icons/outer-eye.svg';
import { ReactComponent as QRPatternIcon } from '../../../../assets/icons/qr-pattern.svg';
import { ReactComponent as SquarePatterIcon } from '../../../../assets/icons/square-pattern.svg';

interface StyleProperties {
  setQrValues: React.Dispatch<React.SetStateAction<any>>;
}

/**
 * QR Style Pattern Component
 * @param {StyleProperties} properties - Component properties
 * @param {React.Dispatch<React.SetStateAction<any>>} properties.setQrValues - The setQrValues prop
 * @returns {JSX.Element} The Style pattern component layout
 */
function Style({ setQrValues }: StyleProperties): JSX.Element {
  const { t } = useTranslation('qrCode');

  const menus = [
    {
      id: 1,
      icon: <QRPatternIcon />,
      label: t('patternStyle'),
      pattern: [
        {
          id: 1,
          icon: <SquarePatterIcon />,
          label: t('square'),
          location: 'qrstyle',
          value: 'squares',
        },
        {
          id: 2,
          icon: <FluidPatterIcon />,
          label: t('fluid'),
          location: 'qrstyle',
          value: 'fluid',
        },
        {
          id: 3,
          icon: <DotPatterIcon />,
          label: t('dot'),
          location: 'qrstyle',
          value: 'dots',
        },
      ],
    },
    {
      id: 2,
      icon: <InnerEye />,
      label: t('patternStyle'),
      pattern: [
        {
          id: 1,
          icon: <InnerEyeSquare className="primary-color-svg" />,
          label: t('square'),
          location: 'innerEye',
          value: 0,
        },
        {
          id: 2,
          icon: <InnerEyeRound className="primary-color-svg" />,
          label: t('roundedSquare'),
          location: 'innerEye',
          value: 3,
        },
        {
          id: 3,
          icon: <InnerEyeCircle className="primary-color-svg" />,
          label: t('circle'),
          location: 'innerEye',
          value: 10,
        },
      ],
    },
    {
      id: 3,
      icon: <OuterEye />,
      label: t('patternStyle'),
      pattern: [
        {
          id: 1,
          icon: <OuterEyeSquare className="primary-color-svg" />,
          label: t('square'),
          location: 'outerEye',
          value: 0,
        },
        {
          id: 2,
          icon: <OuterEyeRound className="primary-color-svg" />,
          label: t('roundedSquare'),
          location: 'outerEye',
          value: 10,
        },
        {
          id: 3,
          icon: <OuterEyeCircle className="primary-color-svg" />,
          label: t('circle'),
          location: 'outerEye',
          value: 20,
        },
      ],
    },
  ];

  /**
   * Handling Style Selection
   * @param {string | number} value - The style value selected
   * @param {string} location - The property key location in qrValues state
   */
  const handleSelect = (value: string | number, location: string) => {
    setQrValues((previous: any) => ({
      ...previous,
      [location]: value,
    }));
  };

  return (
    <div className="style-wrap">
      {menus.map((menu) => (
        <div className="style-menu" key={menu.id}>
          <div className="style-menu-header">
            {menu.icon}
            <span>{menu.label}</span>
          </div>
          <div className="style-menu-selection">
            {menu.pattern.map((pattern) => (
              <div
                role="presentation"
                onClick={() => handleSelect(pattern.value, pattern.location)}
                className="style-pattern"
                key={pattern.id}>
                {pattern.icon}
                <span>{pattern.label}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export default Style;
